<template>
  <div>
    <StructureTable
      bulkActions
      :items="items"
      :headers="headers"
      :totalItems="totalTags"
      :loading="loading"
      :groups="groups"
      :title="$t('organization_structure.tags_group_other')"
      @edit="handleEdit"
      @getItems="handleGetTags"
      @manageGroups="$emit('manageGroups')"
      @createGroup="$emit('createGroup')"
      @apply="handleGroupTags"
      @selectItems="$emit('selectTags', $event)"
    />

    <EditTagDialog
      :sites="sites"
      :groups="groups"
      :tag="selectedTag"
      :visible="showEditDialog"
      :loading="loading || saving"
      @close="handleClose"
      @save="saveTag"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import StructureTable from '../../Shared/StructureTable.vue'
import EditTagDialog from './EditTagDialog.vue'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'TagsTab',

  mixins: [mixin],

  components: {
    StructureTable,
    EditTagDialog,
  },

  props: {
    organizationId: String,
    tags: { type: Array, required: true },
    loading: Boolean,
    totalTags: { Number },
    groups: { required: true, type: Array },
  },

  data() {
    return {
      sites: [],
      saving: false,
      fetching: false,
      selectedTag: null,
      showEditDialog: false,
      options: {},
      headers: [
        {
          text: _.capitalize(this.$t('common.tag_one')),
          align: 'start',
          sortable: true,
          value: 'name',
          show: true,
          width: '100px',
        },

        {
          text: _.capitalize(this.$t('common.tag_group_other')),
          sortable: false,
          value: 'groups',
          align: 'start',
          show: true,
          width: '200px',
        },
        {
          text: _.capitalize(this.$t('common.site_other')),
          sortable: false,
          value: 'sites',
          align: 'start',
          show: true,
          width: '100px',
        },

        {
          text: _.capitalize(this.$t('common.action_other')),
          sortable: false,
          value: 'actions',
          show: true,
          align: 'center',
          width: '80px',
        },
      ],
    }
  },

  async mounted() {
    await this.getSites()
  },

  computed: {
    ...mapGetters(['account']),

    items() {
      return this.tags.map(tag => {
        return {
          ...tag,
          id: tag._id,
        }
      })
    },
  },

  methods: {
    ...mapActions(['getOrganizationStructure']),

    handleGetTags(options) {
      this.options = options
      this.$emit('getTags', options)
    },

    async getGroup(groupId) {
      const response = await api.tagGroups.getById(this.organizationId, groupId)
      return response.data
    },

    async handleGroupTags(data) {
      try {
        this.saving = true
        const group = await this.getGroup(data.group.id)

        const tags = _.uniq([...group.tags, ...data.selectedIds])

        if (tags?.length > this.$constants.maxTagsPerGroup)
          return this.showError(
            this.$t('organization_structure.cannot_add_more_than_100_tags')
          )

        await api.tagGroups.addTags(this.organizationId, data.group.id, {
          tags,
        })
        if (this.account?.organization?._id === this.organizationId) {
          await this.getOrganizationStructure({
            organizationId: this.organizationId,
          })
        }
        this.$emit('getTags', this.options)
        this.$emit('getTagGroups')
        this.showSuccess(
          this.$t('organization_structure.tags_groups_updated_successfully')
        )
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.saving = false
      }
    },

    handleClose() {
      this.selectedTag = null
      this.showEditDialog = false
    },

    handleEdit(tag) {
      this.selectedTag = tag
      this.showEditDialog = true
    },

    async saveTag(tag) {
      this.saving = true
      try {
        const tagToSave = _.omit(tag, ['groups'])

        await api.tag.update(this.organizationId, tag.id, {
          ...tagToSave,
          sites: tag.sites.map(site => site?._id ?? site?.id ?? site),
          name: tagToSave.name.trim(),
        })
        if (this.account?.organization?._id === this.organizationId) {
          await this.getOrganizationStructure({
            organizationId: this.organizationId,
          })
        }
        this.showEditDialog = false
        this.selectedTag = null
        this.$emit('getTags', this.options)
        this.$emit('cleanSearchTerm')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.saving = false
      }
    },

    async getSites() {
      this.fetching = true

      try {
        const response = await api.site.getOrganizationSites(
          this.organizationId,
          { disablePagination: true }
        )

        this.sites = response?.data ?? []
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.fetching = false
      }
    },
  },
}
</script>
